import { useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CabinetHeader } from "../components/CabinetHeader";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { ErrorBanner } from "../components/ErrorBanner";
import { SuccessBanner } from "../components/SuccessBanner";
import {
  getCabinetMe,
  addChild,
  deleteAccount,
  changePassword,
} from "../api/cabinet";
import { clearParentJwt } from "../api/client";

export function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: me, isLoading } = useQuery({
    queryKey: ["cabinet-me"],
    queryFn: getCabinetMe,
  });

  const [showAddChild, setShowAddChild] = useState(false);
  const [showChangePass, setShowChangePass] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)] flex items-center justify-center">
        <p className="text-[var(--color-muted)]">Загрузка…</p>
      </div>
    );
  }
  if (!me) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)]">
        <CabinetHeader />
        <main className="max-w-2xl mx-auto px-6 py-8 text-center">
          <p className="text-[var(--color-accent)]">Ошибка загрузки</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-paper)]">
      <CabinetHeader displayName={me.parent.display_name} />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <Link
          to="/cabinet"
          className="text-sm text-[var(--color-muted)] hover:text-[var(--color-accent)] mb-2 inline-block"
        >
          ← В кабинет
        </Link>
        <h1 className="text-3xl mb-6">Профиль</h1>

        {/* Account info */}
        <section className="bg-white border border-[var(--color-line)] p-6 mb-6">
          <h2
            className="text-xl mb-4"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Учётная запись
          </h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-[var(--color-muted)]">ФИО</dt>
              <dd className="font-medium">{me.parent.display_name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[var(--color-muted)]">Email</dt>
              <dd className="font-medium">{me.parent.email}</dd>
            </div>
            {me.parent.phone && (
              <div className="flex justify-between">
                <dt className="text-[var(--color-muted)]">Телефон</dt>
                <dd className="font-medium">{me.parent.phone}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-[var(--color-muted)]">Зарегистрирован</dt>
              <dd className="font-medium">
                {new Date(me.parent.created_at).toLocaleDateString("ru-RU")}
              </dd>
            </div>
            {me.parent.last_login_at && (
              <div className="flex justify-between">
                <dt className="text-[var(--color-muted)]">Последний вход</dt>
                <dd className="font-medium">
                  {new Date(me.parent.last_login_at).toLocaleString("ru-RU")}
                </dd>
              </div>
            )}
          </dl>
          <div className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setShowChangePass(true)}
            >
              Сменить пароль
            </Button>
          </div>
        </section>

        {/* Children */}
        <section className="bg-white border border-[var(--color-line)] p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <h2
              className="text-xl"
              style={{ fontFamily: "var(--font-serif)" }}
            >
              Мои дети ({me.children.length})
            </h2>
            <Button
              variant="secondary"
              onClick={() => setShowAddChild(true)}
              className="!px-4 !py-2 text-sm"
            >
              + Добавить
            </Button>
          </div>
          {me.children.length > 0 ? (
            <ul className="space-y-2">
              {me.children.map((c) => (
                <li
                  key={c.id}
                  className="flex justify-between items-center py-2 border-b border-[var(--color-line)] last:border-b-0"
                >
                  <div>
                    <div className="font-medium">{c.full_name}</div>
                    {c.class_name && (
                      <div className="text-sm text-[var(--color-muted)]">
                        {c.class_name}
                      </div>
                    )}
                  </div>
                  {!c.is_active && (
                    <span className="text-xs text-[var(--color-muted)] italic">
                      выпускник
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--color-muted)] italic">
              К аккаунту пока не привязан ни один ребёнок. Код для привязки
              выдаёт классный руководитель.
            </p>
          )}
        </section>

        {/* Danger zone */}
        <section className="bg-red-50 border-2 border-[var(--color-accent)] p-6">
          <h2
            className="text-xl mb-2 text-[var(--color-accent)]"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            ⚠ Опасная зона
          </h2>
          <p className="text-sm text-[var(--color-muted)] mb-4">
            Удаление аккаунта необратимо. Все ваши публикации (в любом статусе)
            будут удалены, ребёнок открепится. Это право отзыва согласия на
            обработку ПДн.
          </p>
          <Button
            variant="ghost"
            onClick={() => setShowDelete(true)}
            className="!border-[var(--color-accent)] !text-[var(--color-accent)] hover:!bg-[var(--color-accent)] hover:!text-white"
          >
            🗑 Удалить аккаунт и все данные
          </Button>
        </section>

        {showChangePass && (
          <ChangePasswordModal
            onClose={() => setShowChangePass(false)}
            onDone={() => setShowChangePass(false)}
          />
        )}
        {showAddChild && (
          <AddChildModal
            onClose={() => setShowAddChild(false)}
            onDone={() => {
              queryClient.invalidateQueries({ queryKey: ["cabinet-me"] });
              setShowAddChild(false);
            }}
          />
        )}
        {showDelete && (
          <DeleteAccountModal
            email={me.parent.email}
            onClose={() => setShowDelete(false)}
            onDone={() => {
              clearParentJwt();
              navigate("/");
            }}
          />
        )}
      </main>
    </div>
  );
}

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="text-xl mb-4"
          style={{ fontFamily: "var(--font-serif)" }}
        >
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}

function ChangePasswordModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (newPass.length < 8) {
      setError("Новый пароль должен быть не короче 8 символов");
      return;
    }
    setLoading(true);
    try {
      await changePassword(oldPass, newPass);
      setSuccess("Пароль изменён");
      setTimeout(onDone, 800);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Не удалось сменить пароль");
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Сменить пароль" onClose={onClose}>
      <form onSubmit={onSubmit}>
        <ErrorBanner message={error} />
        <SuccessBanner message={success} />
        <Input
          label="Текущий пароль"
          type="password"
          value={oldPass}
          onChange={(e) => setOldPass(e.target.value)}
          required
          autoComplete="current-password"
        />
        <Input
          label="Новый пароль (мин. 8 символов)"
          type="password"
          value={newPass}
          onChange={(e) => setNewPass(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
        />
        <div className="flex gap-3 justify-end mt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Сменить
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function AddChildModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await addChild(code);
      if (res.already_linked) {
        setSuccess("Ребёнок уже привязан к вашему аккаунту");
      } else {
        setSuccess("Ребёнок успешно добавлен");
      }
      setTimeout(onDone, 1000);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Неверный код или код истёк");
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalShell title="Добавить ребёнка" onClose={onClose}>
      <form onSubmit={onSubmit}>
        <ErrorBanner message={error} />
        <SuccessBanner message={success} />
        <p className="text-sm text-[var(--color-muted)] mb-4">
          Введите код, который выдал классный руководитель. Формат:
          XXXX-XXXX.
        </p>
        <Input
          label="Код привязки"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          required
          autoFocus
          placeholder="ABCD-1234"
          maxLength={20}
        />
        <div className="flex gap-3 justify-end mt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Привязать
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteAccountModal({
  email,
  onClose,
  onDone,
}: {
  email: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (confirmEmail.trim().toLowerCase() !== email.toLowerCase()) {
      setError("Email не совпадает с вашим. Введите его точно.");
      return;
    }
    if (!password) {
      setError("Введите пароль для подтверждения");
      return;
    }
    setLoading(true);
    try {
      await deleteAccount(password);
      onDone();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string | unknown } } };
      const msg = e?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Не удалось удалить");
      setLoading(false);
    }
  }

  return (
    <ModalShell title="⚠ Удаление аккаунта" onClose={onClose}>
      <form onSubmit={onSubmit}>
        <div className="bg-red-50 border-l-4 border-[var(--color-accent)] px-3 py-2 text-sm mb-4">
          Все ваши публикации (в любом статусе) и привязки к детям будут
          удалены. Действие необратимо.
        </div>
        <ErrorBanner message={error} />
        <Input
          label={`Подтвердите email (${email})`}
          type="email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          required
          autoComplete="off"
        />
        <Input
          label="Ваш пароль"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
        <div className="flex gap-3 justify-end mt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="!bg-[var(--color-accent)]"
          >
            Удалить навсегда
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
